//
// Browser-API glue for Web Push (VAPID). Keeps the Push/Notification/
// ServiceWorker plumbing out of the components; the HTTP registration
// stays in services/users.js (refreshUserPushToken with vendor WEBPUSH).
//

const InstallIdStorageKey = 'frmWebPushInstallId'

export function isSupported () {
  return typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
}

// VAPID keys come base64url-encoded; pushManager.subscribe wants raw bytes.
export function urlBase64ToUint8Array (base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// Stable per-browser-installation id, stored in localStorage. Sent as
// appvendorid so the backend's tier-2 refresh match updates the existing
// row in place when the push service rotates the endpoint.
export function getInstallId () {
  try {
    let id = localStorage.getItem(InstallIdStorageKey)
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + '-' + Math.random().toString(16).slice(2)
      localStorage.setItem(InstallIdStorageKey, id)
    }
    return id
  } catch (e) {
    // storage blocked (private mode) — a volatile id still works, the row
    // just won't be tier-2-matched across sessions
    return 'volatile-' + Math.random().toString(16).slice(2)
  }
}

// Coarse, informational only (fills the "model" column like "iPhone" does).
export function browserModel (userAgent) {
  const ua = userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '') || ''
  if (/edg\//i.test(ua)) return 'Edge Browser'
  if (/firefox\//i.test(ua)) return 'Firefox Browser'
  if (/chrome\//i.test(ua)) return 'Chrome Browser'
  if (/safari\//i.test(ua)) return 'Safari Browser'
  return 'Web Browser'
}

// 'unsupported' | 'denied' | 'subscribed' | 'unsubscribed'
export async function getState () {
  if (!isSupported()) {
    return 'unsupported'
  }
  if (Notification.permission === 'denied') {
    return 'denied'
  }
  const subscription = await getSubscription()
  return subscription ? 'subscribed' : 'unsubscribed'
}

export async function getSubscription () {
  if (!isSupported()) {
    return null
  }
  const registration = await navigator.serviceWorker.getRegistration('/')
  if (!registration) {
    return null
  }
  return registration.pushManager.getSubscription()
}

// Registers the service worker, asks for permission (must run in a user
// gesture) and subscribes. Returns { endpoint, p256dh, auth }.
export async function subscribe (vapidPublicKey) {
  const registration = await navigator.serviceWorker.register('/sw.js')
  await navigator.serviceWorker.ready
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('permission-' + permission)
  }
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  })
  return subscriptionFields(subscription)
}

// Unsubscribes browser-side; returns the endpoint that was subscribed
// (the caller needs it to find and delete the server row).
export async function unsubscribe () {
  const subscription = await getSubscription()
  if (!subscription) {
    return null
  }
  const endpoint = subscription.endpoint
  await subscription.unsubscribe()
  return endpoint
}

// { endpoint, p256dh, auth } from a PushSubscription (keys base64url).
export function subscriptionFields (subscription) {
  const json = subscription.toJSON()
  return {
    endpoint: json.endpoint,
    p256dh: json.keys ? json.keys.p256dh : null,
    auth: json.keys ? json.keys.auth : null
  }
}
